/**
 * Settings for the Vest NQ-PERP volume-spike whales (utils/vestSpikes.ts):
 * trailing lookback N, the relative-volume threshold and the baseline
 * (auto / time of day / trailing, same modes as the CME rule). Visibility
 * lives in levelsStore (`vestSpikes`) with the other chart toggles.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clampSpikeSettings, DEFAULT_SPIKE_SETTINGS, PRE_590_DEFAULT_THRESHOLD, SpikeSettings } from '../utils/vestSpikes';
import type { RelVolBaseline } from '../utils/relVolBaseline';

interface VestSpikeSettingsState extends SpikeSettings {
  setLookback: (n: number) => void;
  setThreshold: (x: number) => void;
  setBaseline: (b: RelVolBaseline) => void;
  reset: () => void;
}

/**
 * v0 (before #590) blobs: no baseline (merge fills 'auto'), and the trailing
 * era's default threshold 2.0 means nothing on the time-of-day baseline, so it
 * gives way to the new default. Any other saved threshold is kept.
 */
export function migrateVestSpikeSettings(persisted: unknown, version: number): Partial<SpikeSettings> {
  const p = { ...((persisted && typeof persisted === 'object' ? persisted : {}) as Record<string, unknown>) };
  if (version < 1 && Number(p.threshold) === PRE_590_DEFAULT_THRESHOLD) delete p.threshold;
  return p as Partial<SpikeSettings>;
}

const current = (s: SpikeSettings): SpikeSettings => ({ lookback: s.lookback, threshold: s.threshold, baseline: s.baseline });

export const useVestSpikeSettingsStore = create<VestSpikeSettingsState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_SPIKE_SETTINGS,
      setLookback: (n) => set(clampSpikeSettings({ ...current(get()), lookback: n })),
      setThreshold: (x) => set(clampSpikeSettings({ ...current(get()), threshold: x })),
      setBaseline: (b) => set(clampSpikeSettings({ ...current(get()), baseline: b })),
      reset: () => set({ ...DEFAULT_SPIKE_SETTINGS }),
    }),
    {
      name: 'vest-spike-settings',
      version: 1,
      migrate: (persisted, version) => migrateVestSpikeSettings(persisted, version) as never,
      partialize: (s) => current(s),
      // Missing or junk values (older or hand-edited blobs) fall back per
      // field; blobs saved before the baseline existed get 'auto'.
      merge: (persisted, cur) => ({ ...cur, ...clampSpikeSettings(persisted as Partial<SpikeSettings>) }),
    },
  ),
);
